import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { storage, formatDateTime, formatDate } from '@/lib/storage';
import { STATUS_OPTIONS, SAMPLE_CONCERNS, CONCERN_CATEGORIES } from '@/data/mockData';
import { Search, Megaphone, Eye, Trash2, Filter, FileDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ManageConcerns = () => {
  const [data, setData] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('All');
  const [urgency, setUrgency] = useState('All');
  const [view, setView] = useState(null);

  useEffect(() => {
    let stored = storage.get('concerns', null);
    if (stored === null) {
      const seeded = SAMPLE_CONCERNS.map((c) => ({
        ...c,
        submittedAt: new Date(c.date).toISOString(),
        purok: c.location.split(',')[0]?.trim() || '',
        description: 'Sample concern submission for prototype demo.',
        anonymous: c.name === 'Anonymous',
        timeline: [{ status: c.status, date: new Date(c.date).toISOString(), note: 'Sample seed entry.' }],
      }));
      storage.set('concerns', seeded);
      stored = seeded;
    }
    setData(stored);
  }, []);

  const list = useMemo(() => {
    return data.filter((c) => {
      const ms = status === 'All' || c.status === status;
      const mu = urgency === 'All' || c.urgency === urgency;
      const mq = (c.id + c.category + c.location + (c.name || '')).toLowerCase().includes(q.toLowerCase());
      return ms && mu && mq;
    });
  }, [data, q, status, urgency]);

  const updateStatus = (id, newStatus) => {
    const arr = storage.get('concerns', []);
    const next = arr.map((c) => {
      if (c.id !== id) return c;
      return {
        ...c,
        status: newStatus,
        timeline: [
          ...(c.timeline || []),
          { status: newStatus, date: new Date().toISOString(), note: `Status updated to ${newStatus}.` },
        ],
      };
    });
    storage.set('concerns', next);
    setData(next);
    toast.success(`Status updated to ${newStatus}`);
    if (view && view.id === id) setView(next.find((x) => x.id === id));
  };

  const remove = (id) => {
    if (!window.confirm('Delete this concern?')) return;
    const next = data.filter((c) => c.id !== id);
    storage.set('concerns', next);
    setData(next);
    toast.success('Concern deleted');
  };

  const exportCsv = () => {
    const headers = ['Reference', 'Category', 'Urgency', 'Location', 'Reporter', 'Status', 'Submitted'];
    const rows = list.map((c) => [c.id, c.category, c.urgency || 'Normal', c.location, c.name || '', c.status, formatDateTime(c.submittedAt || c.date)]);
    const csv = [headers, ...rows].map((row) => row.map((cl) => `"${(cl || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `concerns-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div data-testid="admin-concerns-page">
      <AdminPageHeader
        title="Concern Reports"
        description="Track and resolve community-reported concerns."
        actions={
          <Button variant="outline" onClick={exportCsv} data-testid="concerns-export">
            <FileDown className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <Card className="p-4 sm:p-5 mb-5 border-0 shadow-sm-soft">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by reference, location, category..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="concerns-search"
            />
          </div>
          <div className="flex gap-2 items-center">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[180px]" data-testid="concerns-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All statuses</SelectItem>
                {STATUS_OPTIONS.concern.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger className="w-[140px]" data-testid="concerns-filter-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['All', 'Normal', 'Urgent', 'Critical'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-md-soft overflow-hidden">
        {list.length === 0 ? (
          <div className="p-6"><EmptyState icon={Megaphone} title="No concerns found" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id} data-testid={`row-concern-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.id}</TableCell>
                    <TableCell>{c.category}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm">{c.location}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={c.urgency === 'Urgent' || c.urgency === 'Critical' ? 'border-destructive/50 text-destructive bg-destructive/5' : ''}>
                        {c.urgency === 'Urgent' || c.urgency === 'Critical' ? <AlertTriangle className="h-3 w-3 mr-1" /> : null}
                        {c.urgency || 'Normal'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                        <SelectTrigger className="w-[170px] h-8 text-xs" data-testid={`concern-status-trigger-${c.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.concern.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(c.date)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => setView(c)} data-testid={`view-concern-${c.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-concern-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={Boolean(view)} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Concern · {view.id}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <Field label="Category">{view.category}</Field>
                  <Field label="Urgency">{view.urgency || 'Normal'}</Field>
                  <Field label="Status"><StatusBadge status={view.status} /></Field>
                  <Field label="Reporter">{view.name || 'Anonymous'}</Field>
                  <Field label="Contact">{view.contact || '—'}</Field>
                  <Field label="Purok">{view.purok || '—'}</Field>
                  <Field label="Location" full>{view.location}</Field>
                  <Field label="Description" full>{view.description}</Field>
                </div>
                {view.timeline && view.timeline.length > 0 && (
                  <div>
                    <h4 className="font-heading font-semibold mb-2">Timeline</h4>
                    <ol className="relative border-l-2 border-border pl-5 space-y-3">
                      {view.timeline.map((t, i) => (
                        <li key={i} className="relative">
                          <span className="absolute -left-[27px] top-1 h-3.5 w-3.5 rounded-full bg-accent ring-4 ring-background" />
                          <div className="text-sm font-semibold">{t.status}</div>
                          <div className="text-xs text-muted-foreground">{formatDateTime(t.date)}</div>
                          {t.note && <div className="mt-0.5 text-xs text-muted-foreground">{t.note}</div>}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-0.5">{children || '—'}</div>
  </div>
);

export default ManageConcerns;
